<script setup lang="ts">
import { refDebounced } from '@vueuse/core';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEquipmentList } from '@/composables/equipment/useEquipmentList';
import { useDeviceTypes } from '@/composables/equipment/useEquipmentLookups';
import { useIsSystemAdmin } from '@/composables/useIsSystemAdmin';
import { EquipmentService } from '@/services/equipment.services';
import type { IDevice } from '@/types/equipment.types';
import { typeIcon } from '@/utils/equipmentIcons';
import DeviceFormModal from './DeviceFormModal.vue';
import EquipmentConfirmModal from './EquipmentConfirmModal.vue';
import EquipmentField from './EquipmentField.vue';
import EquipmentSelect from './EquipmentSelect.vue';
import EquipmentSiteSelect from './EquipmentSiteSelect.vue';
import EquipmentStoreSelect from './EquipmentStoreSelect.vue';
import EquipmentTabShell from './EquipmentTabShell.vue';

const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();
const { isSystemAdmin } = useIsSystemAdmin();
const { types, typeLabel } = useDeviceTypes();

const search = ref('');
const mac = ref('');
const storeId = ref('');
const siteId = ref('');
const type = ref('');
const status = ref('');
const sortBy = ref<'label' | 'type' | 'status' | 'createdAt'>('label');
const sortOrder = ref<'ASC' | 'DESC'>('ASC');
const debouncedSearch = refDebounced(search, 300);
const debouncedMac = refDebounced(mac, 400);

// Un site appartient à un commerce : changer de commerce efface le site choisi.
watch(storeId, () => (siteId.value = ''));

const typeOptions = computed(() => [
  { value: '', label: t('equipment.allTypes') },
  ...types.value.map((item) => ({
    value: item.type,
    label: item.label,
    icon: typeIcon(item.type),
  })),
]);
const statusOptions = computed(() => [
  { value: '', label: t('equipment.allStatuses') },
  ...(['installed', 'planned', 'retired'] as const).map((value) => ({
    value: value as string,
    label: t(`equipment.status.${value}`),
  })),
]);
const sortOptions = computed(() => [
  { value: 'label' as const, label: t('equipment.sortName') },
  { value: 'type' as const, label: t('equipment.fields.type') },
  { value: 'status' as const, label: t('equipment.fields.status') },
  { value: 'createdAt' as const, label: t('equipment.sortRecent') },
]);
const filtersActive = computed(
  () =>
    !!(
      search.value ||
      mac.value ||
      storeId.value ||
      siteId.value ||
      type.value ||
      status.value
    ),
);
const resetFilters = () => {
  search.value = '';
  mac.value = '';
  storeId.value = '';
  siteId.value = '';
  type.value = '';
  status.value = '';
};

const list = useEquipmentList<IDevice>(
  'devices',
  () => ({
    q: debouncedSearch.value.trim() || undefined,
    mac: debouncedMac.value.trim() || undefined,
    storeIds: storeId.value ? [storeId.value] : undefined,
    siteIds: siteId.value ? [siteId.value] : undefined,
    types: type.value ? [type.value] : undefined,
    statuses: status.value ? [status.value] : undefined,
    sortBy: sortBy.value,
    sortOrder: sortOrder.value,
  }),
  EquipmentService.searchDevices,
);

const creating = defineModel<boolean>('creating', { default: false });
const editing = ref<IDevice | null>(null);
const deleting = ref<IDevice | null>(null);
const formOpen = computed(() => creating.value || !!editing.value);
const closeForm = () => {
  creating.value = false;
  editing.value = null;
};
const onDeleted = async () => {
  deleting.value = null;
  await queryClient.invalidateQueries({ queryKey: ['equipment'] });
};

const addressOf = (device: IDevice) =>
  device.ip ? (device.port ? `${device.ip}:${device.port}` : device.ip) : '—';
</script>

<template>
  <EquipmentTabShell
    v-model:page="list.page.value"
    v-model:page-size="list.pageSize.value"
    :count="list.rows.value.length"
    :total="list.total.value"
    :page-count="list.pageCount.value"
    :is-loading="list.isLoading.value"
    :is-error="list.isError.value"
    empty-icon="pi-desktop"
    @retry="list.refetch()"
  >
    <template #toolbar>
      <EquipmentField :label="t('equipment.search')" class="eqs__wide">
        <div class="eq-search">
          <i class="pi pi-search" />
          <input
            v-model="search"
            class="eq-input"
            type="search"
            :placeholder="t('equipment.devices.searchPlaceholder')"
          />
        </div>
      </EquipmentField>
      <EquipmentField :label="t('equipment.fields.mac')">
        <input
          v-model="mac"
          class="eq-input eq-mono"
          type="search"
          placeholder="aa:bb:cc:dd:ee:ff"
        />
      </EquipmentField>
      <EquipmentField :label="t('equipment.fields.store')">
        <EquipmentStoreSelect v-model="storeId" allow-all />
      </EquipmentField>
      <EquipmentField :label="t('equipment.fields.site')">
        <EquipmentSiteSelect v-model="siteId" :store-id="storeId" allow-all />
      </EquipmentField>
      <EquipmentField :label="t('equipment.fields.type')">
        <EquipmentSelect
          v-model="type"
          :options="typeOptions"
          :search-above="0"
          icon="pi-microchip"
        />
      </EquipmentField>
      <EquipmentField :label="t('equipment.fields.status')">
        <EquipmentSelect
          v-model="status"
          :options="statusOptions"
          :search-above="0"
          icon="pi-circle"
        />
      </EquipmentField>
      <EquipmentField :label="t('equipment.sort')">
        <EquipmentSelect
          v-model="sortBy"
          :options="sortOptions"
          :search-above="0"
          icon="pi-sort-alt"
        />
      </EquipmentField>
      <div class="dtab__tools">
        <button
          type="button"
          class="eq-btn eq-btn--icon"
          :aria-label="t('equipment.toggleOrder')"
          :title="t('equipment.toggleOrder')"
          @click="sortOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC'"
        >
          <i
            :class="
              sortOrder === 'ASC'
                ? 'pi pi-sort-amount-up'
                : 'pi pi-sort-amount-down'
            "
          />
        </button>
        <button
          v-if="filtersActive"
          type="button"
          class="eq-btn eq-btn--ghost"
          @click="resetFilters"
        >
          <i class="pi pi-filter-slash" /> {{ t('equipment.reset') }}
        </button>
      </div>
    </template>

    <table class="eq-table">
      <thead>
        <tr>
          <th>{{ t('equipment.fields.deviceLabel') }}</th>
          <th>{{ t('equipment.fields.type') }}</th>
          <th>{{ t('equipment.fields.status') }}</th>
          <th>{{ t('equipment.fields.site') }}</th>
          <th>{{ t('equipment.fields.address') }}</th>
          <th>{{ t('equipment.fields.mac') }}</th>
          <th v-if="isSystemAdmin" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="device in list.rows.value" :key="device.id">
          <td>
            <div class="eq-cell">
              <span class="eq-chip"
                ><i class="pi" :class="typeIcon(device.type)"
              /></span>
              <span class="eq-stack">
                <strong>{{ device.label }}</strong>
                <small v-if="device.location">{{ device.location }}</small>
              </span>
            </div>
          </td>
          <td>{{ typeLabel(device.type) }}</td>
          <td>
            <span class="eq-badge" :class="`eq-badge--${device.status}`">
              {{ t(`equipment.status.${device.status}`) }}
            </span>
          </td>
          <td>
            {{ device.site.name }}
            <div class="eq-muted">{{ device.store.name }}</div>
          </td>
          <td class="eq-mono">{{ addressOf(device) }}</td>
          <td class="eq-mono">{{ device.mac ?? '—' }}</td>
          <td v-if="isSystemAdmin">
            <div class="eq-actions">
              <button
                type="button"
                class="eq-btn eq-btn--icon"
                :aria-label="t('equipment.edit')"
                @click="editing = device"
              >
                <i class="pi pi-pencil" />
              </button>
              <button
                type="button"
                class="eq-btn eq-btn--icon"
                :aria-label="t('equipment.delete')"
                @click="deleting = device"
              >
                <i class="pi pi-trash" />
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </EquipmentTabShell>

  <DeviceFormModal
    v-if="formOpen"
    :device="editing ?? undefined"
    @close="closeForm"
    @saved="closeForm"
  />
  <EquipmentConfirmModal
    v-if="deleting"
    :title="t('equipment.devices.delete')"
    :message="t('equipment.devices.deleteMessage', { name: deleting.label })"
    :run="
      () =>
        EquipmentService.deleteDevice(
          deleting!.store.id,
          deleting!.site.id,
          deleting!.id,
        )
    "
    @close="deleting = null"
    @done="onDeleted"
  />
</template>

<style scoped>
.dtab__tools {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
